import { useState, type FormEvent } from 'react';
import type { MeetingDetail, SourceReference } from '@shared/contract';
import { api } from '../lib/api';
import { formatDuration } from '../lib/format';
import { References } from './References';

const EXAMPLES = [
  'Vad har vi kommit fram till hittills?',
  'Vilka beslut har fattats?',
  'Vilka frågor är fortfarande öppna?',
  'Sammanfatta de senaste tio minuterna.',
];

export function Questions({ detail, onJump, onAsked }: { detail: MeetingDetail; onJump: (ref: SourceReference) => void; onAsked: () => void }) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const disabled = detail.status === 'created';

  async function ask(question: string) {
    if (!question.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.ask(detail.id, question.trim());
      setText('');
      onAsked();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Frågor om mötet</h3>
      <p className="muted small">Svaren bygger endast på det som transkriberats från det här mötet.</p>
      <div className="qa-list">
        {detail.questions.map((q) => (
          <div key={q.id} className="qa">
            <p className="q">{q.question}</p>
            {q.status === 'pending' && <p className="a muted">Svar genereras…</p>}
            {q.status === 'failed' && <p className="a error-text">Frågan kunde inte besvaras. Försök igen.</p>}
            {q.status === 'answered' && (
              <div className={`a${q.not_in_material ? ' not-found' : ''}`}>
                <p>{q.answer}</p>
                <p className="muted small">
                  Baserat på transkription fram till {formatDuration(q.covers_until_ms)}{' '}
                  <References refs={q.source_references} onJump={onJump} />
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <form
        className="row"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void ask(text);
        }}
      >
        <input
          placeholder={disabled ? 'Starta mötet för att ställa frågor' : 'Ställ en fråga om mötet…'}
          value={text}
          maxLength={1000}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
        />
        <button className="btn primary" disabled={disabled || busy || !text.trim()}>
          Fråga
        </button>
      </form>
      {!disabled && detail.questions.length === 0 && (
        <div className="examples">
          {EXAMPLES.map((ex) => (
            <button key={ex} className="chip" onClick={() => ask(ex)} disabled={busy}>
              {ex}
            </button>
          ))}
        </div>
      )}
      {error && <div className="alert error small">{error}</div>}
    </section>
  );
}
